import React, { useState, useEffect, useContext } from "react";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import { AuthContext, useAuth } from '../context/AuthContext';
import useApi from '../hooks/useApi';

const AddStudentModal = ({
  show,
  onClose,
  onEnrollSuccess,
  isSchoolAdmin = true,
  schoolId,
  type = "DS",
  
}) => {
  const navigate = useNavigate();
  const { loginUser } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    zipcode: "",
    dob: "",
    gender: "",
    adminNote: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [selectedCourseDuration, setSelectedCourseDuration] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [includeWeekends, setIncludeWeekends] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  const { user, viewMode, setViewMode, saveAuthToken } = useAuth();
  const { bearerAuth, publicAuth } = useApi();

  // Enrollment For options
  const [enrollmentFor, setEnrollmentFor] = useState("self");

  const getLatestAllowedDob = () => {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 18);
    return today.toISOString().split("T")[0];
  };

  const isAtLeast18 = (dob) => {
    if (!dob) return false;
    const birthDate = new Date(dob);
    if (Number.isNaN(birthDate.getTime())) return false;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 18;
  };

  // Helper function to check if date is after today
  const isDateAfterToday = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);
    return selectedDate > today;
  };

  const enrollmentOptions = [
    { value: "self", label: "Self" },
    { value: "parent", label: "Parent" },
    { value: "other", label: "Other" }
  ];

  useEffect(() => {
    if (show) {
      resetForm();
      fetchCourses();
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = "15px";
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [show]);

  useEffect(() => {
    if (selectedCourseId && selectedCourseDuration > 0) {
      calculateDateRange();
    }
  }, [selectedCourseId, selectedCourseDuration, includeWeekends, selectedDate]);

  const fetchCourses = async () => {
    try {
      const res = await publicAuth(`courses`);
      const list = res.data?.courses || res.data?.data || [];
      setCourses(list);
    } catch (err) {
      console.error("Failed to fetch courses:", err);
    }
  };

  const getCourseDisplayPrice = (course) => {
    const value = course?.price?.total_price ?? course?.total_price ?? course?.price;
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return "Price N/A";
    }
    return `₹${Math.round(numeric)}`;
  };

  const calculateDateRange = () => {
    if (!selectedCourseDuration) {
      setDateRange([]);
      return;
    }
    let daysToAdd = selectedCourseDuration;
    let date = new Date(selectedDate);
    let daysCounted = 0;
    let range = [new Date(date)];
    while (daysCounted < daysToAdd - 1) {
      date.setDate(date.getDate() + 1);
      if (includeWeekends || (date.getDay() !== 0 && date.getDay() !== 6)) {
        daysCounted++;
        range.push(new Date(date));
      }
    }
    setDateRange(range);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      zipcode: "",
      dob: "",
      gender: "",
      adminNote: "",
    });
    setSelectedCourseId("");
    setSelectedCourseDuration(0);
    setSelectedDate(new Date());
    setSelectedTimeSlot(null);
    setErrors({});
    setIsLoading(false);
    setIncludeWeekends(false);
    setDateRange([]);
    // Reset enrollment for
    setEnrollmentFor("self");
  };

  // Enrollment For change handler
  const handleEnrollmentForChange = (e) => {
    setEnrollmentFor(e.target.value);
    setErrors((prev) => ({ ...prev, enrollmentFor: "" }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const { name, email, phone, zipcode, dob, gender, adminNote } = formData;
    const newErrors = {};
    
    if (!name.trim()) newErrors.name = "Name is required";
    else if (!/^[a-zA-Z\s]+$/.test(name))
      newErrors.name = "Only letters and spaces allowed";
    
    if (!email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = "Invalid email";
    
    if (!phone.trim()) newErrors.phone = "Phone is required";
    else if (!/^[6-9]\d{9}$/.test(phone))
      newErrors.phone = "Enter valid 10-digit Indian number";
    
    if (!zipcode) newErrors.zipcode = "Enter a 6-digit ZIP code";
    else if (!/^[1-9][0-9]{5}$/.test(zipcode))
      newErrors.zipcode = "Invalid Indian ZIP code";

    if (!dob) newErrors.dob = "Date of birth is required";
    else if (!isAtLeast18(dob)) newErrors.dob = "Student must be at least 18 years old";
    if (!gender) newErrors.gender = "Gender is required";
    
    if (!selectedCourseId) newErrors.course = "Please select a course";
    if (!enrollmentFor) newErrors.enrollmentFor = "Please select enrollment for";
    if (!selectedTimeSlot) newErrors.timeSlot = "Please select a time slot";
    if (!adminNote.trim()) newErrors.adminNote = "Admin note is required";

    // Validate start date is after today
    if (!isDateAfterToday(selectedDate)) {
      newErrors.startDate = "Start date must be after today";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsLoading(true);
    try {
      // Single API call to users/add
      const payload = {
        full_name: formData.name,
        email: formData.email,
        contact: formData.phone,
        zipcode: formData.zipcode,
        dob: formData.dob,
        gender: formData.gender,
        course_id: selectedCourseId,
        enrollment_for: enrollmentFor,
        inc_sat_sun: includeWeekends,
        admin_exact_start_at: `${selectedDate.toISOString().split("T")[0]} ${selectedTimeSlot.start_time}`,
        admin_time_slot: `${selectedTimeSlot.start_time} - ${selectedTimeSlot.end_time}`,
        admin_note: formData.adminNote,
        type: type
      };
      
      const response = await bearerAuth(`driving-school/add-student`, "POST", payload);
      
      if (response.data.status) {
        await Swal.fire({
          icon: "success",
          title: "Student Added Successfully!",
          text: "Student has been registered and enrolled successfully.",
          showConfirmButton: false,
          timer: 2000,
        });
        
        if (onEnrollSuccess) {
          onEnrollSuccess();
        }
        resetForm();
        onClose();
      } else {
        // Show backend error message in SweetAlert
        Swal.fire({
          icon: "error",
          title: "Registration Failed",
          text: response.data.message || "Student registration failed",
          confirmButtonColor: "#0d6efd",
        });
      }
    } catch (err) {
      // FIXED: Always show the exact backend error message
      console.log("Error response:", err.response); // For debugging
      
      // Extract error message from response
      let errorMessage = "Something went wrong";
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Show the exact error message from backend
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#0d6efd",
      });
      
      // If it's the specific date error, highlight the date field
      if (errorMessage.toLowerCase().includes("admin exact start at") || 
          errorMessage.toLowerCase().includes("date after today")) {
        setErrors(prev => ({
          ...prev,
          startDate: "Please select a future date for the start of the course"
        }));
      }
      
      // If it's the active enrollment error, you might want to highlight something specific
      if (errorMessage.toLowerCase().includes("active enrollment")) {
        // You could add specific handling here if needed
        console.log("User already has active enrollment");
      }
      
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTimeSlots = async (date) => {
    try {
      const formattedDate = date.toISOString().split("T")[0];
      const res = await bearerAuth(`check-timeslots`, "POST", {
        date: formattedDate,
        assignSchool: schoolId || user?.id,
      });
      const slots = res.data?.slots || res.data?.data || [];
      setAvailableSlots(slots);
      setSelectedTimeSlot(null);
    } catch (err) {
      console.error("Slot fetch error:", err);
      setAvailableSlots([]);
    }
  };

  const formatSlotLabel = (slot) => {
    const base = `${slot.start_time} - ${slot.end_time}`;
    const capacity = Number(slot.capacity);
    const bookedCount = Number(slot.booked_count || 0);
    if (capacity > 0) {
      return `${base} (${bookedCount}/${capacity})${slot.booked ? " - Full" : ""}`;
    }
    return base;
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    // Clear any previous date error
    setErrors(prev => ({ ...prev, startDate: "" }));
    fetchTimeSlots(date);
    calculateDateRange();
  };

  const handleCourseSelect = (e) => {
    const courseId = e.target.value;
    setSelectedCourseId(courseId);
    setErrors((prev) => ({ ...prev, course: "" }));
    if (courseId) {
      const selectedCourse = courses.find((c) => c.id === courseId);
      if (selectedCourse) {
        setSelectedCourseDuration(parseInt(selectedCourse.duration) || 0);
      }
    } else {
      setSelectedCourseDuration(0);
    }
  };

  const toggleIncludeWeekends = () => {
    setIncludeWeekends(!includeWeekends);
  };

  const renderDayContents = (day, date) => {
    const isInRange = dateRange.some((d) => d.toDateString() === date.toDateString());
    const isSelected = selectedDate && selectedDate.toDateString() === date.toDateString();
    const isPastDate = !isDateAfterToday(date);
    
    return (
      <div
        style={{
          backgroundColor: isSelected ? "#0d6efd" : isInRange ? "#e6f2ff" : "transparent",
          fontWeight: isSelected ? "bold" : "normal",
          color: isSelected ? "#fff" : isInRange ? "#0d6efd" : isPastDate ? "#ccc" : "inherit",
          borderRadius: "50%",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: isPastDate ? "not-allowed" : "pointer",
        }}
      >
        {day}
      </div>
    );
  };

  if (!show) return null;

  return (
    <>  
      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.7);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1050;
          overflow-y: auto;
          padding: 20px;
        }
        .modal-box {
          background: white;
          border-radius: 8px;
          padding: 25px;
          width: 100%;
          max-width: 800px;
          box-shadow: 0 5px 15px rgba(0,0,0,0.2);
          position: relative;
          overflow: hidden;
          margin: auto;
        }
        .registerclose {
          position: absolute;
          top: 1px;
          right: 20px;
          font-size: 1.5rem;
          background: none;
          border: none;
          cursor: pointer;
          color: #6c757d;
          transition: color 0.2s;
          z-index: 1;
        }
        .registerclose:hover {
          color: #495057;
        }
        .modal-content {
          position: relative;
          z-index: 1;
        }
        .form-control {
          border-radius: 6px;
          border: 1px solid #ced4da;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .form-control:focus {
          border-color: #86b7fe;
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
        }
        .btn-primary {
          background-color: #0d6efd;
          border: none;
          border-radius: 6px;
          padding: 10px 20px;
          font-weight: 500;
          transition: background-color 0.2s;
        }
        .btn-primary:hover {
          background-color: #0b5ed7;
        }
        .text-danger {
          font-size: 0.85rem;
          margin-top: 5px;
        }
        .input-group-text {
          border-radius: 6px 0 0 6px !important;
        }
        .custom-calendar {
          width: 100%;
          border: none;
          box-shadow: none;
        }
        .react-datepicker {
          font-family: inherit;
          border: none;
        }
        .react-datepicker__header {
          background-color: #f8f9fa;
          border-bottom: none;
        }
        .react-datepicker__day--selected,
        .react-datepicker__day--keyboard-selected {
          background-color: #0d6efd;
          color: white;
        }
        .react-datepicker__day--highlight-range {
          background-color: #e6f2ff;
          color: #0d6efd;
          font-weight: normal;
        }
        .react-datepicker__day--highlight-range:hover {
          background-color: #cce0ff;
        }
        .react-datepicker__day--selected,
        .react-datepicker__day--highlight-range.react-datepicker__day--selected {
          background-color: #0d6efd;
          color: white;
          font-weight: bold;
        }
        .time-slots-container {
          max-height: 200px;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }
        .time-slot-btn {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          min-height: 44px;
          font-size: 0.9rem;
          padding: 8px 12px;
        }
        @media (max-width: 768px) {
          .modal-box {
            padding: 20px;
            max-width: 95%;
          }
          .form-control {
            padding: 10px 12px;
          }
          .btn-primary {
            padding: 10px;
          }
        }
        @media (max-width: 576px) {
          .modal-box {
            padding: 15px;
          }
          .date-time-section .row {
            flex-direction: column;
          }
          .date-time-section .col-md-5,
          .date-time-section .col-md-7 {
            width: 100%;
          }
          .date-time-section .col-md-5 {
            margin-bottom: 15px;
          }
          .time-slot-btn {
            font-size: 0.85rem;
            padding: 6px 10px;
            min-height: 40px;
          }
        }
        .form-section {
          margin-bottom: 20px;
          padding: 15px;
          border-radius: 8px;
          background-color: #f8f9fa;
        }
        .form-section-title {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 15px;
          color: #495057;
          border-bottom: 1px solid #dee2e6;
          padding-bottom: 8px;
        }
        .textarea-field {
          min-height: 100px;
          resize: vertical;
        }
      `}</style>   
      
      <div className="modal-overlay">
        <div className="modal-box">
          <button
            onClick={() => {
              resetForm();
              onClose();
            }}
            className="registerclose"
            aria-label="Close modal"
          >
            ×
          </button>
          
          <div className="modal-content">
            <h2 className="text-center mb-4" style={{ color: "#343a40" }}>
              Add New Student
            </h2>
            
            <form onSubmit={handleSubmit}>
              {/* Personal Information Section */}
              <div className="form-section">
                <h5 className="form-section-title">Personal Information</h5>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Full Name*</label>
                    <input
                      type="text"
                      name="name"
                      className={`form-control ${errors.name ? "is-invalid" : ""}`}
                      placeholder="Full Name"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={isLoading}
                    />
                    {errors.name && <div className="text-danger">{errors.name}</div>}
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Email ID*</label>
                    <input
                      type="email"
                      name="email"
                      className={`form-control ${errors.email ? "is-invalid" : ""}`}
                      placeholder="Email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={isLoading}
                    />
                    {errors.email && <div className="text-danger">{errors.email}</div>}
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Mobile Number*</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">+91</span>
                      </div>
                      <input
                        type="tel"
                        name="phone"
                        className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                        placeholder="Mobile Number"
                        value={formData.phone}
                        onChange={handleChange}
                        maxLength={10}
                        disabled={isLoading}
                      />
                    </div>
                    {errors.phone && <div className="text-danger">{errors.phone}</div>}
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Zipcode*</label>
                    <input
                      type="text"
                      name="zipcode"
                      className={`form-control ${errors.zipcode ? "is-invalid" : ""}`}
                      placeholder="Zipcode"
                      value={formData.zipcode}
                      onChange={handleChange}
                      maxLength={6}
                      disabled={isLoading}
                    />
                    {errors.zipcode && <div className="text-danger">{errors.zipcode}</div>}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Date of Birth*</label>
                    <input
                      type="date"
                      name="dob"
                      className={`form-control ${errors.dob ? "is-invalid" : ""}`}
                      value={formData.dob}
                      max={getLatestAllowedDob()}
                      onChange={handleChange}
                      disabled={isLoading}
                    />
                    {errors.dob && <div className="text-danger">{errors.dob}</div>}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Gender*</label>
                    <select
                      name="gender"
                      className={`form-control ${errors.gender ? "is-invalid" : ""}`}
                      value={formData.gender}
                      onChange={handleChange}
                      disabled={isLoading}
                    >
                      <option value="">-- Select Gender --</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                    {errors.gender && <div className="text-danger">{errors.gender}</div>}
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Enrollment For*</label>
                    <select
                      className={`form-control ${errors.enrollmentFor ? "is-invalid" : ""}`}
                      value={enrollmentFor}
                      onChange={handleEnrollmentForChange}
                      disabled={isLoading}
                    >
                      {enrollmentOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.enrollmentFor && <div className="text-danger">{errors.enrollmentFor}</div>}
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label">Admin Note*</label>
                    <textarea
                      name="adminNote"
                      className={`form-control textarea-field ${errors.adminNote ? "is-invalid" : ""}`}
                      placeholder="Enter admin notes, instructions, or special requirements for this student..."
                      value={formData.adminNote}
                      onChange={handleChange}
                      disabled={isLoading}
                      rows="3"
                    />
                    {errors.adminNote && <div className="text-danger">{errors.adminNote}</div>}
                    <small className="text-muted">
                      Add any important notes or instructions for the student's enrollment.
                    </small>
                  </div>
                </div>
              </div>
              
              {/* Course Selection Section */}
              <div className="form-section">
                <h5 className="form-section-title">Course Selection</h5>
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <label className="form-label">Choose a Course*</label>
                    <select
                      className={`form-control ${errors.course ? "is-invalid" : ""}`}
                      value={selectedCourseId}
                      onChange={handleCourseSelect}
                      disabled={isLoading}
                    >
                      <option value="">-- Select a Course --</option>
                      {courses.map((course) => (
                        <option key={course.id} value={course.id}>
                          {course.title} ({course.duration} days) - {getCourseDisplayPrice(course)}
                        </option>
                      ))}
                    </select>
                    {errors.course && <div className="text-danger">{errors.course}</div>}
                  </div>
                </div>
              </div>
              
              {/* Date & Time Selection Section */}
              <div className="form-section date-time-section">
                <h5 className="form-section-title">Schedule Selection</h5>
                
                <div className="form-check mb-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="includeWeekends"
                    checked={includeWeekends}
                    onChange={toggleIncludeWeekends}
                    disabled={!selectedCourseId}
                  />
                  <label className="form-check-label" htmlFor="includeWeekends">
                    Include Saturdays & Sundays
                  </label>
                </div>
                
                <div className="row">
                  <div className="col-md-5 mb-3 mb-md-0">
                    <div className="card h-100">
                      <div className="card-body">
                        <h6 className="card-title">Select Date*</h6>
                        <DatePicker
                          selected={selectedDate}
                          onChange={handleDateChange}
                          inline
                          className="w-100 custom-calendar"
                          renderDayContents={renderDayContents}
                          dayClassName={(date) => {
                            const isInRange = dateRange.some(
                              (d) => d.toDateString() === date.toDateString()
                            );
                            return isInRange ? "react-datepicker__day--highlight-range" : "";
                          }}
                          minDate={new Date(Date.now() + 86400000)} // Disable today and past dates
                        />
                        {errors.startDate && (
                          <div className="text-danger mt-2">{errors.startDate}</div>
                        )}
                        {selectedCourseDuration > 0 && dateRange.length > 0 && (
                          <div className="mt-3 small text-muted">
                            <p className="mb-1">
                              <strong>Course Duration:</strong> {selectedCourseDuration} days
                            </p>
                            <p className="mb-0">
                              <strong>Period:</strong> {dateRange[0].toDateString()} to{" "}
                              {dateRange[dateRange.length - 1].toDateString()}
                              {!includeWeekends && " (excluding weekends)"}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-md-7">
                    <div className="card h-100">
                      <div className="card-body">
                        <h6 className="card-title">Available Time Slots*</h6>
                        {availableSlots.length === 0 ? (
                          <div className="alert alert-warning">
                            No slots available for selected date
                          </div>
                        ) : (
                          <div className="time-slots-container">
                            <div className="row">
                              {availableSlots.map((slot) => (
                                <div key={slot.id} className="col-6 mb-2">
                                  <button
                                    type="button"
                                    className={`btn w-100 time-slot-btn ${
                                      selectedTimeSlot?.id === slot.id
                                        ? "btn-primary"
                                        : slot.booked
                                        ? "btn-outline-secondary disabled"
                                        : "btn-outline-primary"
                                    }`}
                                    onClick={() => !slot.booked && setSelectedTimeSlot(slot)}
                                    disabled={slot.booked}
                                  >
                                    {formatSlotLabel(slot)}
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {errors.timeSlot && <div className="text-danger mt-2">{errors.timeSlot}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    resetForm();
                    onClose();
                  }}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Adding Student...
                    </>
                  ) : (
                    "ADD STUDENT"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddStudentModal;